/* Indexes homepage modules: shared, idempotent storefront and theme-editor lifecycle.
 * Only data-ix-editable-home markup is owned here. Legacy homepage scripts remain isolated.
 * Gesture thresholds, RTL navigation, accessibility and default timings match the original home.
 */
(() => {
  'use strict';
  if (window.__ixHomeEditorBound) return;
  window.__ixHomeEditorBound = true;

  const controllers = new Map();
  const selectedDetails = new Map();
  const editableSelector = '[data-ix-editable-home]';

  const collect = (scope, selector) => {
    const nodes = [];
    if (scope?.nodeType === 1 && scope.matches(selector)) nodes.push(scope);
    if (scope?.querySelectorAll) nodes.push(...scope.querySelectorAll(selector));
    return nodes;
  };

  const sectionIdFor = (node) => {
    const owner = node.closest('[data-section-id]');
    if (owner?.dataset.sectionId) return owner.dataset.sectionId;
    const section = node.closest('[id^="shopify-section-"]');
    return section ? section.id.replace(/^shopify-section-/, '') : '';
  };

  const intervalFor = (node, fallback) => {
    const interval = Number(node.dataset.interval);
    return Number.isFinite(interval) && interval > 0 && interval <= 2147483647 ? interval : fallback;
  };

  const belongsToEvent = (node, event, sectionId = sectionIdFor(node)) => {
    if (event.detail?.sectionId != null && sectionId) {
      return String(event.detail.sectionId) === String(sectionId);
    }
    return event.target === node || Boolean(event.target?.contains?.(node)) || Boolean(node.contains?.(event.target));
  };

  // Touch leaves buttons focused on mobile; only keyboard focus should hold rotation.
  function rotationFocus(region, setPauseReason, options) {
    let pointerFocus = false;
    document.addEventListener('pointerdown', () => { pointerFocus = true; }, { ...options, capture: true });
    document.addEventListener('keydown', () => {
      pointerFocus = false;
      if (region.contains(document.activeElement)) setPauseReason('focus', true);
    }, { ...options, capture: true });
    return () => region.contains(document.activeElement) && !pointerFocus;
  }

  function initCarousel(carousel) {
    if (!carousel || controllers.has(carousel)) return;
    carousel.dataset.carouselInitializing = 'true';

    const slides = Array.from(carousel.querySelectorAll('[data-carousel-slide]'));
    const viewport = carousel.querySelector('.ix-home-carousel__viewport');
    const pauseButton = carousel.querySelector('[data-carousel-pause]');
    const pauseIcon = carousel.querySelector('[data-carousel-pause-icon]');
    const playIcon = carousel.querySelector('[data-carousel-play-icon]');
    const status = carousel.querySelector('[data-carousel-status]');
    const pauseLabel = carousel.querySelector('[data-carousel-pause-label]');
    const dots = Array.from(carousel.querySelectorAll('[data-carousel-dot]'));
    if (!viewport || !slides.length || !pauseButton || !pauseIcon || !playIcon || !status) {
      delete carousel.dataset.carouselInitializing;
      return;
    }

    const multiple = slides.length > 1;
    const autoplay = multiple && carousel.dataset.autoplay === 'true'; // Native editor preference.
    const interval = intervalFor(carousel, 8000);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    const listeners = new AbortController();
    const listenerOptions = { signal: listeners.signal };
    const pauseReasons = new Set(['offscreen']);
    let activeIndex = 0;
    let autoTimer = 0;
    let cooldownTimer = 0;
    let observer = null;
    let userPaused = carousel.dataset.carouselUserPaused === 'true';
    if (userPaused) pauseReasons.add('user');
    let pointer = null;
    let suppressClickUntil = 0;
    let destroyed = false;
    let focusFrame = 0;

    const clearAutoTimer = () => {
      window.clearTimeout(autoTimer);
      autoTimer = 0;
    };

    const scheduleAuto = () => {
      clearAutoTimer();
      if (destroyed || !carousel.isConnected || !autoplay || reducedMotion.matches || document.hidden || pauseReasons.size) return;
      autoTimer = window.setTimeout(() => {
        if (destroyed || !carousel.isConnected || document.hidden || reducedMotion.matches || pauseReasons.size) return;
        autoTimer = 0;
        showSlide(activeIndex + 1, false, true);
        scheduleAuto();
      }, interval);
    };

    const setPauseReason = (reason, paused) => {
      if (destroyed) return;
      if (paused) pauseReasons.add(reason);
      else pauseReasons.delete(reason);
      scheduleAuto();
    };

    const startCooldown = () => {
      if (destroyed || !multiple) return;
      window.clearTimeout(cooldownTimer);
      setPauseReason('cooldown', true);
      cooldownTimer = window.setTimeout(() => setPauseReason('cooldown', false), 4500);
    };

    // IX_BANNER_AUTOPLAY_V10: a finite settle at each gated slide change, never a loop animation.
    const clearBannerMotion = () => {
      slides.forEach((slide) => slide.classList.remove('is-motion-entering', 'is-motion-leaving'));
    };
    carousel.addEventListener('animationend', (event) => {
      if (!['ix-banner-enter', 'ix-banner-leave'].includes(event.animationName)) return;
      event.target.closest('[data-carousel-slide]')?.classList.remove('is-motion-entering', 'is-motion-leaving');
    }, listenerOptions);

    const showSlide = (requestedIndex, announce = false, animate = announce) => {
      if (destroyed) return;
      const previousIndex = activeIndex;
      carousel.dataset.motionDirection = requestedIndex >= previousIndex ? 'next' : 'previous';
      activeIndex = ((requestedIndex % slides.length) + slides.length) % slides.length;
      carousel.dataset.carouselActiveBlock = slides[activeIndex].dataset.blockId || String(activeIndex);
      clearBannerMotion();
      if (animate && activeIndex !== previousIndex && !reducedMotion.matches && !document.hidden) {
        slides[previousIndex].classList.add('is-motion-leaving');
        slides[activeIndex].classList.add('is-motion-entering');
      }
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        const link = slide.querySelector('a');
        if (link) link.tabIndex = isActive ? 0 : -1;
      });
      dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });
      if (announce) status.textContent = `الشريحة ${activeIndex + 1} من ${slides.length}`;
    };

    const showKeyboardSlide = (requestedIndex) => {
      startCooldown();
      showSlide(requestedIndex, true);
      const activeLink = slides[activeIndex].querySelector('a');
      if (activeLink) activeLink.focus({ preventScroll: true });
    };

    const syncPauseControl = () => {
      const stopped = userPaused || reducedMotion.matches || !autoplay;
      pauseButton.setAttribute('aria-pressed', stopped ? 'true' : 'false');
      pauseButton.setAttribute('aria-label', userPaused ? 'تشغيل الشرائح تلقائيًا' : 'إيقاف التشغيل التلقائي للشرائح');
      if (pauseLabel) pauseLabel.textContent = reducedMotion.matches ? 'الحركة متوقفة' : (userPaused ? 'تشغيل الحركة' : 'إيقاف الحركة');
      if (reducedMotion.matches) pauseButton.setAttribute('aria-label', 'الحركة متوقفة لتفضيل تقليل الحركة');
      pauseButton.setAttribute('title', pauseButton.getAttribute('aria-label'));
      pauseIcon.hidden = stopped;
      playIcon.hidden = !stopped;
    };

    const syncMotionPreference = () => {
      if (destroyed) return;
      if (reducedMotion.matches) clearBannerMotion();
      if (reducedMotion.matches || !autoplay) {
        pauseReasons.add('reduced-motion');
        pauseButton.hidden = !autoplay;
        pauseButton.disabled = true;
      } else {
        pauseReasons.delete('reduced-motion');
        pauseButton.hidden = false;
        pauseButton.disabled = false;
      }
      syncPauseControl();
      scheduleAuto();
    };

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => { startCooldown(); showSlide(index, true); }, listenerOptions);
    });

    carousel.querySelector('[data-carousel-next]')?.addEventListener('click', () => { startCooldown(); showSlide(activeIndex + 1, true); }, listenerOptions);
    carousel.querySelector('[data-carousel-previous]')?.addEventListener('click', () => { startCooldown(); showSlide(activeIndex - 1, true); }, listenerOptions);

    pauseButton.addEventListener('click', () => {
      if (!autoplay || reducedMotion.matches) return;
      userPaused = !userPaused;
      carousel.dataset.carouselUserPaused = String(userPaused);
      if (!userPaused) { pauseReasons.delete('focus'); pauseReasons.delete('hover'); }
      setPauseReason('user', userPaused);
      syncPauseControl();
      status.textContent = userPaused ? 'تم إيقاف التشغيل التلقائي للشرائح.' : 'تم تشغيل الشرائح تلقائيًا.';
    }, listenerOptions);

    carousel.addEventListener('keydown', (event) => {
      if (!multiple || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showKeyboardSlide(activeIndex + 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showKeyboardSlide(activeIndex - 1);
      }
    }, listenerOptions);

    const hasKeyboardFocus = rotationFocus(carousel, setPauseReason, listenerOptions);
    carousel.addEventListener('focusin', () => setPauseReason('focus', hasKeyboardFocus()), listenerOptions);
    carousel.addEventListener('focusout', () => {
      window.cancelAnimationFrame(focusFrame);
      focusFrame = window.requestAnimationFrame(() => {
        focusFrame = 0;
        if (!destroyed) setPauseReason('focus', hasKeyboardFocus());
      });
    }, listenerOptions);
    carousel.addEventListener('pointerenter', () => {
      if (fineHover.matches) setPauseReason('hover', true);
    }, listenerOptions);
    carousel.addEventListener('pointerleave', () => {
      if (fineHover.matches) setPauseReason('hover', false);
    }, listenerOptions);

    viewport.addEventListener('pointerdown', (event) => {
      if (!multiple || !event.isPrimary || pointer || (event.pointerType === 'mouse' && event.button !== 0)) return;
      pointer = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        dragging: false,
        vertical: false
      };
      setPauseReason('pointer', true);
    }, listenerOptions);

    viewport.addEventListener('pointermove', (event) => {
      if (!pointer || event.pointerId !== pointer.id) return;
      pointer.lastX = event.clientX;
      pointer.lastY = event.clientY;
      const deltaX = pointer.lastX - pointer.startX;
      const deltaY = pointer.lastY - pointer.startY;
      if (!pointer.dragging && !pointer.vertical) {
        if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
        if (Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) {
          pointer.vertical = true;
          return;
        }
        pointer.dragging = true;
        viewport.classList.add('is-dragging');
        try { viewport.setPointerCapture(event.pointerId); } catch (error) {}
      }
      if (pointer.dragging && event.cancelable) event.preventDefault();
    }, { signal: listeners.signal, passive: false });
    viewport.addEventListener('dragstart', (event) => event.preventDefault(), listenerOptions);

    const clearPointer = (event, cancelled = false) => {
      if (!pointer) return;
      if (event && typeof event.pointerId === 'number' && event.pointerId !== pointer.id) return;
      const finished = pointer;
      if (event && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
        finished.lastX = event.clientX;
        finished.lastY = event.clientY;
      }
      pointer = null;
      viewport.classList.remove('is-dragging');
      if (viewport.hasPointerCapture?.(finished.id)) {
        try { viewport.releasePointerCapture(finished.id); } catch (error) {}
      }
      if (finished.dragging) {
        const deltaX = finished.lastX - finished.startX;
        const deltaY = finished.lastY - finished.startY;
        suppressClickUntil = Date.now() + 350;
        if (!cancelled && Math.abs(deltaX) >= 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
          showSlide(activeIndex + (deltaX < 0 ? 1 : -1), true);
        }
      }
      setPauseReason('pointer', false);
      startCooldown();
    };

    viewport.addEventListener('pointerup', (event) => clearPointer(event, false), listenerOptions);
    viewport.addEventListener('pointercancel', (event) => clearPointer(event, true), listenerOptions);
    viewport.addEventListener('lostpointercapture', (event) => clearPointer(event, true), listenerOptions);
    window.addEventListener('pointerup', (event) => clearPointer(event, false), listenerOptions);
    window.addEventListener('pointercancel', (event) => clearPointer(event, true), listenerOptions);
    window.addEventListener('blur', () => clearPointer(null, true), listenerOptions);
    viewport.addEventListener('click', (event) => {
      if (Date.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    }, { signal: listeners.signal, capture: true });

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearPointer(null, true); clearBannerMotion();
        window.clearTimeout(cooldownTimer); pauseReasons.delete('cooldown');
      }
      setPauseReason('hidden', document.hidden);
    };
    document.addEventListener('visibilitychange', onVisibilityChange, listenerOptions);
    onVisibilityChange();

    if ('IntersectionObserver' in window) {
      try {
        observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          setPauseReason('offscreen', !entry || !entry.isIntersecting || entry.intersectionRatio < 0.35);
        }, { threshold: [0, 0.35] });
        observer.observe(carousel);
      } catch (error) {
        try { observer?.disconnect(); } catch (disconnectError) {}
        observer = null;
        // Without visibility observation, keep automatic movement safely stopped.
      }
    } else {
      // Without visibility observation, keep automatic movement safely stopped.
    }

    const onMotionChange = () => syncMotionPreference();
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotionChange);
    else reducedMotion.addListener(onMotionChange);

    const controller = {
      sectionId: sectionIdFor(carousel),
      setSelected(selected) { setPauseReason('editor-section', selected); },
      selectBlock(blockId, selected) {
        const index = slides.findIndex((slide) => slide.dataset.blockId === blockId);
        if (index < 0) return;
        setPauseReason(`editor-block:${blockId}`, selected);
        if (selected) showSlide(index, true, false);
      },
      destroy() {
        if (destroyed) return;
        destroyed = true;
        clearPointer(null, true);
        clearAutoTimer();
        clearBannerMotion();
        window.clearTimeout(cooldownTimer);
        window.cancelAnimationFrame(focusFrame);
        observer?.disconnect();
        listeners.abort();
        if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', onMotionChange);
        else reducedMotion.removeListener(onMotionChange);
        delete carousel.dataset.carouselReady;
        delete carousel.dataset.carouselInitializing;
        controllers.delete(carousel);
      }
    };
    controllers.set(carousel, controller);

    if (carousel.contains(document.activeElement)) pauseReasons.add('focus');
    const rememberedIndex = slides.findIndex((slide, index) => (slide.dataset.blockId || String(index)) === carousel.dataset.carouselActiveBlock);
    const markedIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
    showSlide(rememberedIndex >= 0 ? rememberedIndex : Math.max(0, markedIndex), false);
    syncPauseControl();
    syncMotionPreference();
    scheduleAuto();
    carousel.dataset.carouselReady = 'true';
    delete carousel.dataset.carouselInitializing;
  }

  function initRail(rail) {
    if (!rail || controllers.has(rail)) return;
    rail.dataset.categoryRailInitializing = 'true';

    const track = rail.querySelector('[data-category-track]');
    const items = Array.from(rail.querySelectorAll('[data-category-item]'));
    const pauseButton = rail.querySelector('[data-category-pause]');
    const pauseIcon = rail.querySelector('[data-category-pause-icon]');
    const playIcon = rail.querySelector('[data-category-play-icon]');
    const status = rail.querySelector('[data-category-status]');
    if (!track || !items.length || !pauseButton || !pauseIcon || !playIcon || !status) {
      delete rail.dataset.categoryRailInitializing;
      return;
    }

    const multiple = items.length > 1;
    const autoplay = multiple && rail.dataset.autoplay === 'true';
    const interval = intervalFor(rail, 6500);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    const listeners = new AbortController();
    const listenerOptions = { signal: listeners.signal };
    const pauseReasons = new Set(['offscreen']);
    let activeIndex = 0;
    let autoFrame = 0;
    let lastFrame = 0;
    let loopPosition = 0;
    let cycleWidth = 0;
    let pixelsPerSecond = 28;
    let loopRtl = true;
    const loopCopies = [];
    let cooldownTimer = 0;
    let scrollTimer = 0;
    let resizeTimer = 0;
    let userPaused = false;
    let manualScrollPending = false;
    let suppressClickUntil = 0;
    let activePointerId = null;
    let drag = null;
    let observer = null;
    let destroyed = false;
    let focusFrame = 0;

    const hasOverflow = () => track.scrollWidth > track.clientWidth + 2;

    // A second visual sequence makes the reverse pass seamless. Original links
    // remain the only keyboard/screen-reader sequence and keep their editor IDs.
    const prepareLoop = () => {
      if (!loopCopies.length) {
        items.forEach((item) => {
          const copy = item.cloneNode(true);
          copy.removeAttribute('data-category-item');
          copy.setAttribute('data-category-copy', '');
          copy.setAttribute('aria-hidden', 'true');
          [copy, ...copy.querySelectorAll('*')].forEach((node) => {
            node.removeAttribute('id');
            node.removeAttribute('data-block-id');
            [...node.attributes].forEach((attr) => {
              if (attr.name.startsWith('data-shopify')) node.removeAttribute(attr.name);
            });
          });
          copy.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(node => node.tabIndex = -1);
          track.append(copy);
          loopCopies.push(copy);
        });
        track.classList.add('has-category-loop');
      }
      loopRtl = window.getComputedStyle(track).direction === 'rtl';
      const first = items[0].getBoundingClientRect();
      const copy = loopCopies[0].getBoundingClientRect();
      cycleWidth = Math.abs(first.left - copy.left);
      pixelsPerSecond = Math.min(36, Math.max(16, first.width / (interval / 1000 * 2)));
      loopPosition = track.scrollLeft;
    };

    const clearAutoTimer = () => {
      window.cancelAnimationFrame(autoFrame);
      autoFrame = 0;
      lastFrame = 0;
      track.classList.remove('is-auto-scrolling');
    };

    const nearestIndex = () => {
      const trackRect = track.getBoundingClientRect();
      const edge = document.dir === 'rtl' || window.getComputedStyle(track).direction === 'rtl' ? trackRect.right : trackRect.left;
      let closest = 0;
      let closestDistance = Infinity;
      [...items, ...loopCopies].forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const itemEdge = window.getComputedStyle(track).direction === 'rtl' ? rect.right : rect.left;
        const distance = Math.abs(itemEdge - edge);
        if (distance < closestDistance) {
          closest = index % items.length;
          closestDistance = distance;
        }
      });
      return closest;
    };

    const announceIndex = (index) => {
      const title = items[index]?.dataset.categoryTitle || '';
      status.textContent = title ? `الفئة ${index + 1} من ${items.length}: ${title}` : `الفئة ${index + 1} من ${items.length}`;
    };

    const scrollToIndex = (requestedIndex, options = {}) => {
      if (destroyed) return;
      const index = Math.max(0, Math.min(items.length - 1, requestedIndex));
      activeIndex = index;
      const trackRect = track.getBoundingClientRect();
      let targetRect = items[index].getBoundingClientRect();
      if (!options.focus && loopCopies[index]) {
        const copyRect = loopCopies[index].getBoundingClientRect();
        if (Math.abs(copyRect.left - trackRect.left) < Math.abs(targetRect.left - trackRect.left)) targetRect = copyRect;
      }
      const rtl = window.getComputedStyle(track).direction === 'rtl';
      // Scroll only the rail: autoplay must never pull the page up or down.
      track.scrollTo({
        left: track.scrollLeft + (rtl ? targetRect.right - trackRect.right : targetRect.left - trackRect.left),
        behavior: reducedMotion.matches ? 'auto' : (options.behavior || 'smooth'),
      });
      if (options.announce) announceIndex(index);
      if (options.focus) {
        const link = items[index].querySelector('a');
        if (link) link.focus({ preventScroll: true });
      }
    };

    const canRotate = () => !destroyed && rail.isConnected && autoplay && !document.hidden && !reducedMotion.matches && !pauseReasons.size && hasOverflow();
    const advanceLoop = (timestamp) => {
      if (!canRotate()) { clearAutoTimer(); return; }
      if (lastFrame) {
        // Positive scrollLeft in RTL moves artwork left, opposite the hero entry.
        const delta = pixelsPerSecond * Math.min(timestamp - lastFrame, 64) / 1000;
        loopPosition += loopRtl ? delta : -delta;
        if (loopRtl) {
          while (loopPosition > 0) loopPosition -= cycleWidth;
          while (loopPosition < -cycleWidth) loopPosition += cycleWidth;
        } else {
          while (loopPosition < 0) loopPosition += cycleWidth;
          while (loopPosition > cycleWidth) loopPosition -= cycleWidth;
        }
        track.scrollLeft = loopPosition;
      }
      lastFrame = timestamp;
      autoFrame = window.requestAnimationFrame(advanceLoop);
    };
    const scheduleAuto = () => {
      clearAutoTimer();
      if (!canRotate()) return;
      prepareLoop();
      if (cycleWidth < 1) return;
      track.classList.add('is-auto-scrolling');
      autoFrame = window.requestAnimationFrame(advanceLoop);
    };

    const setPauseReason = (reason, paused) => {
      if (destroyed) return;
      if (paused) pauseReasons.add(reason);
      else pauseReasons.delete(reason);
      scheduleAuto();
    };

    const startCooldown = (duration = 4200) => {
      if (destroyed || !multiple) return;
      window.clearTimeout(cooldownTimer);
      setPauseReason('cooldown', true);
      cooldownTimer = window.setTimeout(() => setPauseReason('cooldown', false), duration);
    };

    const syncPauseControl = () => {
      pauseButton.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
      pauseButton.setAttribute('aria-label', userPaused ? 'تشغيل التمرير التلقائي للفئات' : 'إيقاف التمرير التلقائي للفئات');
      pauseIcon.hidden = userPaused;
      playIcon.hidden = !userPaused;
    };

    const syncMotionPreference = () => {
      if (destroyed) return;
      if (reducedMotion.matches || !autoplay) {
        pauseReasons.add('reduced-motion');
        pauseButton.hidden = true;
        pauseButton.disabled = true;
      } else {
        pauseReasons.delete('reduced-motion');
        pauseButton.disabled = false;
        pauseButton.hidden = !hasOverflow();
      }
      scheduleAuto();
    };

    pauseButton.addEventListener('click', () => {
      if (!autoplay || reducedMotion.matches) return;
      userPaused = !userPaused;
      if (!userPaused) { pauseReasons.delete('focus'); pauseReasons.delete('hover'); }
      setPauseReason('user', userPaused);
      syncPauseControl();
      status.textContent = userPaused ? 'تم إيقاف التمرير التلقائي للفئات.' : 'تم تشغيل التمرير التلقائي للفئات.';
    }, listenerOptions);

    track.addEventListener('keydown', (event) => {
      if (!multiple || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      let requestedIndex = null;
      if (event.key === 'ArrowLeft') requestedIndex = Math.min(items.length - 1, activeIndex + 1);
      else if (event.key === 'ArrowRight') requestedIndex = Math.max(0, activeIndex - 1);
      else if (event.key === 'Home') requestedIndex = 0;
      else if (event.key === 'End') requestedIndex = items.length - 1;
      if (requestedIndex === null) return;
      event.preventDefault();
      startCooldown();
      scrollToIndex(requestedIndex, { announce: true, focus: true });
    }, listenerOptions);

    const hasKeyboardFocus = rotationFocus(rail, setPauseReason, listenerOptions);
    rail.addEventListener('focusin', (event) => {
      const item = event.target.closest('[data-category-item]');
      if (item) activeIndex = items.indexOf(item);
      setPauseReason('focus', hasKeyboardFocus());
    }, listenerOptions);

    rail.addEventListener('focusout', () => {
      window.cancelAnimationFrame(focusFrame);
      focusFrame = window.requestAnimationFrame(() => {
        focusFrame = 0;
        if (!destroyed) setPauseReason('focus', hasKeyboardFocus());
      });
    }, listenerOptions);

    rail.addEventListener('pointerenter', () => {
      if (fineHover.matches) setPauseReason('hover', true);
    }, listenerOptions);

    rail.addEventListener('pointerleave', () => {
      if (fineHover.matches) setPauseReason('hover', false);
    }, listenerOptions);

    track.addEventListener('pointerdown', (event) => {
      if (!multiple || !event.isPrimary || activePointerId !== null || (event.pointerType === 'mouse' && event.button !== 0)) return;
      activePointerId = event.pointerId;
      setPauseReason('pointer', true);
      manualScrollPending = true;
      if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
        drag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          startScrollLeft: track.scrollLeft,
          dragging: false
        };
      }
    }, listenerOptions);

    track.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const deltaX = event.clientX - drag.startX;
      const deltaY = event.clientY - drag.startY;
      if (!drag.dragging) {
        if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
        if (Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
        drag.dragging = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(event.pointerId); } catch (error) {}
      }
      if (event.cancelable) event.preventDefault();
      track.scrollLeft = drag.startScrollLeft - deltaX;
    }, listenerOptions);

    const finishPointer = (event, cancelled = false) => {
      if (activePointerId === null) return;
      if (event && typeof event.pointerId === 'number' && event.pointerId !== activePointerId) return;
      const finishedPointerId = activePointerId;
      activePointerId = null;
      if (drag && finishedPointerId === drag.pointerId) {
        if (drag.dragging) {
          suppressClickUntil = Date.now() + 350;
          if (!destroyed) {
            activeIndex = nearestIndex();
            scrollToIndex(activeIndex, { announce: !cancelled, behavior: 'smooth' });
          }
        }
      }
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture?.(finishedPointerId)) {
        try { track.releasePointerCapture(finishedPointerId); } catch (error) {}
      }
      drag = null;
      setPauseReason('pointer', false);
      startCooldown();
    };

    track.addEventListener('pointerup', (event) => finishPointer(event, false), listenerOptions);
    track.addEventListener('pointercancel', (event) => finishPointer(event, true), listenerOptions);
    track.addEventListener('lostpointercapture', (event) => finishPointer(event, true), listenerOptions);
    window.addEventListener('pointerup', (event) => finishPointer(event, false), listenerOptions);
    window.addEventListener('pointercancel', (event) => finishPointer(event, true), listenerOptions);
    window.addEventListener('blur', () => finishPointer(null, true), listenerOptions);

    track.addEventListener('click', (event) => {
      if (Date.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    }, { signal: listeners.signal, capture: true });

    track.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) > 0 || event.shiftKey) {
        manualScrollPending = true;
        startCooldown();
      }
    }, { signal: listeners.signal, passive: true });

    track.addEventListener('scroll', () => {
      if (autoFrame && !manualScrollPending) return;
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        if (destroyed) return;
        activeIndex = nearestIndex();
        if (manualScrollPending) {
          announceIndex(activeIndex);
          manualScrollPending = false;
        }
      }, 140);
    }, { signal: listeners.signal, passive: true });

    const onVisibilityChange = () => {
      if (document.hidden) finishPointer(null, true);
      setPauseReason('hidden', document.hidden);
    };
    document.addEventListener('visibilitychange', onVisibilityChange, listenerOptions);
    onVisibilityChange();

    if ('IntersectionObserver' in window) {
      try {
        observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          setPauseReason('offscreen', !entry || !entry.isIntersecting || entry.intersectionRatio < 0.45);
        }, { threshold: [0, 0.45] });
        observer.observe(rail);
      } catch (error) {
        try { observer?.disconnect(); } catch (disconnectError) {}
        observer = null;
        pauseReasons.delete('offscreen');
      }
    } else {
      pauseReasons.delete('offscreen');
    }

    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (destroyed) return;
        activeIndex = Math.min(nearestIndex(), items.length - 1);
        syncMotionPreference();
      }, 160);
    };
    window.addEventListener('resize', onResize, listenerOptions);

    const onMotionChange = () => syncMotionPreference();
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotionChange);
    else reducedMotion.addListener(onMotionChange);

    const controller = {
      sectionId: sectionIdFor(rail),
      setSelected(selected) { setPauseReason('editor-section', selected); },
      selectBlock(blockId, selected) {
        const index = items.findIndex((item) => item.dataset.blockId === blockId);
        if (index < 0) return;
        setPauseReason(`editor-block:${blockId}`, selected);
        if (selected) scrollToIndex(index, { behavior: 'auto', announce: true });
      },
      destroy() {
        if (destroyed) return;
        destroyed = true;
        finishPointer(null, true);
        clearAutoTimer();
        window.clearTimeout(cooldownTimer);
        window.clearTimeout(scrollTimer);
        window.clearTimeout(resizeTimer);
        window.cancelAnimationFrame(focusFrame);
        observer?.disconnect();
        listeners.abort();
        if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', onMotionChange);
        else reducedMotion.removeListener(onMotionChange);
        loopCopies.forEach(copy => copy.remove());
        track.classList.remove('has-category-loop');
        delete rail.dataset.categoryRailReady;
        delete rail.dataset.categoryRailInitializing;
        controllers.delete(rail);
      }
    };
    controllers.set(rail, controller);

    syncPauseControl();
    syncMotionPreference();
    rail.dataset.categoryRailReady = 'true';
    delete rail.dataset.categoryRailInitializing;
  }

  function boot(scope = document) {
    collect(scope, '[data-ix-home-carousel]').forEach((node) => {
      if (node.closest(editableSelector)) initCarousel(node);
    });
    collect(scope, '[data-ix-category-rail]').forEach((node) => {
      if (node.closest(editableSelector)) initRail(node);
    });
  }

  function blockForEvent(event) {
    const blockId = event.detail?.blockId;
    const target = event.target?.nodeType === 1 ? event.target : null;
    const direct = target?.closest('[data-block-id]');
    if (direct?.closest(editableSelector) && belongsToEvent(direct, event) &&
        (blockId == null || direct.dataset.blockId === String(blockId))) return direct;
    if (blockId == null) return null;
    return collect(document, `${editableSelector}[data-block-id], ${editableSelector} [data-block-id]`)
      .find((node) => node.dataset.blockId === String(blockId) && belongsToEvent(node, event)) || null;
  }

  function restoreDetails(details) {
    const prior = selectedDetails.get(details);
    if (!prior) return;
    // Preserve a manual close made while selected; otherwise restore the previous state.
    if (details.open) details.open = prior.open;
    selectedDetails.delete(details);
  }

  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
    if (collect(event.target, editableSelector).length) {
      document.dispatchEvent(new CustomEvent('indexes:products-rendered'));
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((controller, node) => {
      if (belongsToEvent(node, event, controller.sectionId)) controller.destroy();
    });
    selectedDetails.forEach((prior, details) => {
      if (belongsToEvent(details, event, prior.sectionId)) restoreDetails(details);
    });
  });

  ['select', 'deselect'].forEach((action) => {
    const selected = action === 'select';
    document.addEventListener(`shopify:section:${action}`, (event) => {
      if (selected) boot(event.target);
      controllers.forEach((controller, node) => {
        if (belongsToEvent(node, event, controller.sectionId)) controller.setSelected(selected);
      });
    });

    document.addEventListener(`shopify:block:${action}`, (event) => {
      const block = blockForEvent(event);
      const blockId = event.detail?.blockId != null ? String(event.detail.blockId) : block?.dataset.blockId;
      if (!blockId) return;
      if (selected && block) {
        const section = block.closest('[id^="shopify-section-"]') || block.closest(editableSelector);
        boot(section);
      }
      controllers.forEach((controller, node) => {
        if (belongsToEvent(node, event, controller.sectionId)) controller.selectBlock(blockId, selected);
      });
      if (!block) return;
      const details = block.matches('details') ? block : block.querySelector('details');
      if (!details || !details.closest('.ix-service-panel')) return;
      if (selected) {
        if (!selectedDetails.has(details)) {
          selectedDetails.set(details, { open: details.open, sectionId: sectionIdFor(details) });
        }
        details.open = true;
      } else restoreDetails(details);
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
})();




